// Mixin for common dashboard breakdown functionality
import {
  getAdminReports,
  getAdminReportById,
  getSurgeonReports,
  getSurgeonReportsById,
  getDistributorReports,
  getDistributorReportById,
  getHospitalAdminReports,
  getHospitalAdminReportsById,
} from 'raypro_ui/src/api/reports'
import {
  exportReports,
  exportReportsAdmin,
  exportReportsDistributor,
  exportReportsHospitalAdmin,
} from 'raypro_ui/src/api/csvs'
import {mapGetters} from 'vuex'

export default {
  data() {
    return {
      dataLoaded: false,
      iolFilters: [null, null, null],
      globalTimeSpan: 12,
    }
  },

  props: {
    iols: Array,
    contextSurgeon: Object,
    globalReports: Array
  },

  async created() {
    this.updateFiltersFromParent()
    await this.getReports()
    this.setDownloadOptions()
  },

  watch: {
    async contextSurgeon() {
      await this.getReports()
    },

    async '$parent.filterQuery'() {
      this.updateFiltersFromParent()
      await this.getReports()
    }
  },

  computed: {
    ...mapGetters(['authUser', 'isAdmin', 'isDistributor', 'isHospitalAdmin']),

    filterParams() {
      let filter = this.$parent.filterQuery ? `${this.$parent.filterQuery}&` : ''
      //filter = this.getYearQueryParameter() ? `${filter}${this.getYearQueryParameter()}&` : filter
      return filter
    },

    reportUser() {
      if (this.isHospitalAdmin && this.contextSurgeonValid) {
        return this.contextSurgeon
      }

      return this.authUser
    },

    contextSurgeonValid() {
      return (this.contextSurgeon && Object.keys(this.contextSurgeon).includes('id'))
    },
  },

  methods: {
    async getAllReportsPromise(filters) {
      let promise = null
      const payload = {
        id: this.reportUser.id,
        filters
      }

      if (this.isAdmin) {
        promise = getAdminReports(payload)
      } else if (this.isDistributor) {
        promise = getDistributorReports(payload)
      } else if (this.isHospitalAdmin && this.contextSurgeonValid === false) {
        promise = getHospitalAdminReports(payload)
      } else {
        promise = getSurgeonReports(payload)
      }

      return promise
    },

    async getReportByIdPromise(filters) {
      let promise = null
      const payload = {
        id: this.reportUser.id,
        report_id: this.$route.params.report_id,
        filters: filters
      }

      if (this.isAdmin) {
        promise = getAdminReportById(payload)
      } else if (this.isDistributor) {
        promise = getDistributorReportById(payload)
      } else if (this.isHospitalAdmin && this.contextSurgeonValid === false) {
        promise = getHospitalAdminReportsById(payload)
      } else {
        promise = getSurgeonReportsById(payload)
      }

      return promise
    },

    async getExportReportPromise(id, name) {
      let promise = null
      const payload = {
        filters: this.filterParams.replace('?', '&'),
        report_id: id,
        report_name: name,
        surgeon_id: this.reportUser.id
      }

      if (this.isAdmin) {
        promise = exportReportsAdmin(payload)
      } else if (this.isDistributor) {
        promise = exportReportsDistributor(payload)
      } else if (this.isHospitalAdmin && this.contextSurgeonValid === false) {
        promise = exportReportsHospitalAdmin(payload)
      } else {
        promise = exportReports(payload)
      }

      return promise
    },

    getSubReportFilterParams(index) {
      if (this.filterParams.includes('?iol=') === false) {
        return ''
      }

      let filter = this.filterParams.replace('?iol=', '')
      const rest = filter.includes('&') ? filter.slice(filter.indexOf('&')) : ''
      filter = filter.includes('&') ? filter.split('&')[0] : filter
      const split = filter.split(',')

      filter = ((split.length - 1) >= index) ? split[index] : ''
      if (filter === '') {
        return ''
      }

      return (rest !== '') ? `?iol=${filter}${rest}` : `?iol=${filter}`
    },

    getModelNamesFromFilterParams() {
      let models = []
      for (var i = 0; i < 3; i++) {
        let filter = this.getSubReportFilterParams(i)
        if (filter) {
          filter = filter.replace('?iol=', '')
          filter = (filter.includes('&')) ? filter.slice(0, filter.indexOf('&')) : filter
          models.push(filter)
        }
      }
      return models
    },

    getTimesForReport(report) {
      return report.times.map((item) => {
        return {
          'x': item.mon,
          'y': item.count
        }
      })
    },

    getTimesForBenchmark(report) {
      const times = report.times.map((item) => {
        return {
          'x': item.mon,
          'y': report.benchmark
        }
      })

      if (times.length >= 3) {
        return [times.at(0), times.at(-1)]
      }

      return times
    },

    updateFiltersFromParent() {
      this.setTimeSpan()
      if (!this.filterParams.includes('?iol=')) {
        this.iolFilters.forEach((_, index) => {
          this.iolFilters[index] = null
        })
      }

      let models = this.getModelNamesFromFilterParams()
      this.iolFilters.forEach((item, index) => {
        if (item) {
          if (!models.includes(item.model)) {
            this.iolFilters[index] = null
          }
        } else {
          models.every((model) => {
            if (!this.iolFilters.find((x) => x && x.model === model)) {
              this.iolFilters[index] = this.iols.find((x) => x.model === model)
              return false
            }
            return true
          })
        }
      })

      this.updateSubReports()
    },

    setTimeSpan() {
      if (this.filterParams.includes('time_period=30')) {
        this.globalTimeSpan = 1
      } else if (this.filterParams.includes('time_period=90')) {
        this.globalTimeSpan = 3
      } else if (this.filterParams.includes('time_period=180')) {
        this.globalTimeSpan = 6
      } else {
        this.globalTimeSpan = 12
      }
    },

    truncateReportToTimeSpan(report) {
      let newReport = JSON.parse(JSON.stringify(report))
      if (this.globalTimeSpan !== 12) {
        newReport.times = newReport.times.filter((item, idx) => {
          return 11 - idx < this.globalTimeSpan
        })
      }
      return newReport
    },

    updateSubReports() {
      // Override in component
    },

    setDownloadOptions() {
      this.$emit('setDownloadOptions', false)
    },

    showIolFilter(index) {
      return (this.iolFilters[index] === null || this.iolFilters[index] === '')
    },

    downloadResult(result) {
      const disposition = result.headers['content-disposition']
      const matches = /"([^"]*)"/.exec(disposition)
      const filename = matches !== null && matches[1] ? matches[1] : 'file.csv'
      const blob = new Blob([result.data], {type: 'text/csv'})
      const link = document.createElement('a')

      link.href = window.URL.createObjectURL(blob)
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    getGlobalReport(name, slug) {
      let reports = JSON.parse(JSON.stringify(this.globalReports.filter((item) => {
        return item.name === name && item.slug === slug
      })))

      reports.forEach((item) => {
        item.name = 'Global Benchmark'
      })

      return reports
    },
  },
}



// WEBPACK FOOTER //
// ./src/mixins/breakdown.js